/*
Site Video
*/

class SiteVideo extends HTMLElement {
  constructor() {
    super();
    this.observe();
  }

  observe() {
    if ("IntersectionObserver" in window) {
      const videoObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(function (video) {
          const $video = video.target;
          if (video.isIntersecting) {
            if ($video.readyState === 4) {
              $video.play();
            } else {
              [].slice
                .call($video.querySelectorAll("source"))
                .forEach((source) => {
                  source.src = source.dataset.src;
                });
              $video.load();
            }
          } else {
            if (!$video.paused) {
              $video.pause();
            }
          }
        });
      });

      videoObserver.observe(this.querySelector("video"));
    }
  }
}

customElements.define("site-video", SiteVideo);

/*
Site Image
*/

class SiteImage extends HTMLElement {
  constructor() {
    super();
    this.observe();
  }

  observe() {
    if ("IntersectionObserver" in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((el) => {
          if (el.isIntersecting) {
            const srcset =
              el.target.dataset.srcset || el.target.getAttribute("srcset");
            const preloadedImage = new Image();
            preloadedImage.srcset = srcset;
            preloadedImage.onload = () => {
              el.target.srcset = srcset;
              this.classList.add("active");
              imageObserver.unobserve(this);
            };
          }
        });
      });

      const img = this.querySelector(".image-full");
      imageObserver.observe(img);
    }
  }
}

customElements.define("site-image", SiteImage);

/*
Carousel Prev
*/

class CarouselPrev extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("You need to have a child button to go to previous!");
    }

    this.addEventListener("click", (e) => {
      const carousel = e.target.closest("site-carousel");

      if (carousel) {
        carousel.previous();
      } else {
        console.error("<carousel-prev> needs to be a child of <site-carousel>");
      }
    });
  }
}

customElements.define("carousel-prev", CarouselPrev);

/*
Carousel Next
*/

class CarouselNext extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("You need to have a child button to go to next!");
    }

    this.addEventListener("click", (e) => {
      const carousel = e.target.closest("site-carousel");
      if (carousel) {
        carousel.next();
      } else {
        console.error("<carousel-next> needs to be a child of <site-carousel>");
      }
    });
  }
}

customElements.define("carousel-next", CarouselNext);

/*
Carousel Pager
*/

class CarouselPager extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const pagerButtons = Array.from(this.querySelectorAll("button"));

    if (!pagerButtons.length > 1) {
      console.warn("No need for a pager with only one item!");
    }

    pagerButtons.forEach((pager, i) => {
      pager.addEventListener("click", (e) => {
        const carousel = e.target.closest("site-carousel");

        if (carousel) {
          carousel.slideTo(i);
        } else {
          console.error(
            "<carousel-pager> needs to be a child of <site-carousel>"
          );
        }
      });
    });
  }
}

customElements.define("carousel-pager", CarouselPager);

/*
 * Site Carousel
 */

class SiteCarousel extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
  }

  connectedCallback() {
    const config = this.dataset.config
      ? JSON.parse(this.dataset.config)
      : { slidesPerView: 1, spaceBetween: 20, loop: true };

    this.swiper = new Swiper(`#${this.dataset.id} .swiper`, config);
  }

  previous() {
    if (this.swiper) {
      this.swiper.slidePrev();
    }
  }

  next() {
    if (this.swiper) {
      this.swiper.slideNext();
    }
  }

  slideTo(i) {
    if (this.swiper) {
      this.swiper.slideTo(i);
    }
  }

  handleSlideChange() {
    // we can have multiple pagers in one carousel
    const pagers = Array.from(this.querySelectorAll("carousel-pager"));

    if (this.swiper) {
      this.swiper.on("slideChange", () => {
        pagers.forEach((pager) => {
          const buttons = Array.from(pager.querySelectorAll("button"));
          const nextPager = buttons[this.swiper.realIndex];

          buttons.forEach((button) => button.classList.remove("active"));
          nextPager.classList.add("active");
        });
      });
    }
  }
}

customElements.define("site-carousel", SiteCarousel);

/*
Site Header
*/

class SiteHeader extends HTMLElement {
  constructor() {
    super();
    this.header = null;
  }

  connectedCallback() {
    this.header = this.querySelector(".site-header");
    window.addEventListener("scroll", () => {
      const scrollY = window.scrollY || window.pageYOffset;

      if (scrollY >= window.innerHeight * 0.95) {
        this.header.classList.add("active");
      } else {
        this.header.classList.remove("active");
      }
    });
  }
}

customElements.define("site-header", SiteHeader);

/*
Cart Toggle
*/

class CartToggle extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector("button")) {
      console.warn("Cart toggle component should have a button as a child!");
    }

    this.addEventListener("click", (e) => {
      const cart = document.querySelector("site-cart");
      if (cart) {
        cart.toggleCart();
      }
    });
  }
}

customElements.define("cart-toggle", CartToggle);

/*
Cart Add
*/

class CartAdd extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.querySelector("form").addEventListener("submit", async (e) => {
      e.preventDefault();

      const $id = e.target.querySelector('[name="id"]');
      const $qty = e.target.querySelector('[name="quantity"]');
      const $sellingPlan = e.target.querySelector('[name="selling_plan"]');
      const qty = $qty ? $qty.value : 1;
      const sellingPlan =
        $sellingPlan && !!$sellingPlan.value ? $sellingPlan.value : null;

      if ($id) {
        const cart = document.querySelector("site-cart");
        if (cart) {
          await cart.addVariantToCart($id.value, qty, sellingPlan);
        }
      }
    });
  }
}

customElements.define("cart-add", CartAdd);

/*
Cart Update
*/

class CartUpdate extends HTMLElement {
  constructor() {
    super();
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  connectedCallback() {
    this.addEventListener(
      "change",
      this.debounce(async (e) => {
        const cart = document.querySelector("site-cart");
        const idx = e.target.dataset.index;
        const val = e.target.value;

        if (cart) {
          await cart.updateQuantity(idx, val);
        }
      }, 300)
    );
  }
}

customElements.define("cart-update", CartUpdate);

/*
Cart Remove
*/

class CartRemove extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", async (e) => {
      e.preventDefault();

      const cart = document.querySelector("site-cart");
      const idx = this.dataset.index;

      if (cart) {
        await cart.updateQuantity(idx, 0);
      }
    });
  }
}

customElements.define("cart-remove", CartRemove);

/*
Site Cart
*/

class SiteCart extends HTMLElement {
  constructor() {
    super();
    this.cart = null;
  }

  connectedCallback() {
    this.cart = document.querySelector(".site-cart");
  }

  openCart() {
    if (this.cart) {
      this.cart.classList.add("active");
      document.body.style.overflow = "hidden";
    }
  }

  closeCart() {
    if (this.cart) {
      this.cart.classList.remove("active");
      document.body.removeAttribute("style");
    }
  }

  toggleCart() {
    const header = document.querySelector("site-header");
    if (this.cart.classList.contains("active")) {
      this.closeCart();
      header.cartOrFlyoutClose("cart");
    } else {
      this.openCart();
      header.cartOrFlyoutOpen("cart");
    }
  }

  getCartSections() {
    return [
      {
        section: "main-cart-items",
        selector: "#main-cart-items",
      },
      {
        section: "main-cart-footer",
        selector: "#main-cart-footer",
      },
      {
        section: "main-cart-header",
        selector: "#main-cart-header",
      },
    ];
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  renderCartSections(sections) {
    const getSections = this.getCartSections();
    const sectionsArr = Object.keys(sections);

    sectionsArr.forEach((sectionKey) => {
      const activeSection = getSections.find((s) => s.section === sectionKey);
      const elToReplace = document.querySelector(activeSection.selector);
      if (elToReplace) {
        elToReplace.innerHTML = this.getSectionInnerHTML(
          sections[sectionKey],
          activeSection.selector
        );
      }
    });
  }

  buildConfig(body) {
    return {
      body: body,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: `application/json`,
      },
    };
  }

  addVariantToCart(id, quantity = 1, sellingPlan) {
    const items = [
      sellingPlan
        ? { id, quantity, selling_plan: sellingPlan }
        : { id, quantity },
    ];
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ items, sections });
    const config = this.buildConfig(body);

    if (!this.cart.classList.contains("active")) {
      this.toggleCart();
    }

    return fetch(`/cart/add`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.handleErrorMessage(response.description);
          return;
        }
        this.renderCartSections(response.sections);
      })
      .catch((e) => {
        // handle error
      });
  }

  updateQuantity(line, quantity) {
    const items = Array.from(document.querySelectorAll("cart-item"));
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ line, quantity, sections });
    const config = this.buildConfig(body);
    const item = items[line - 1];
    const price = item.querySelector(".cart-price");
    const loader = item.querySelector(".cart-loader");

    if (price) {
      price.classList.add("hidden");
    }
    if (loader) {
      loader.classList.remove("hidden");
    }

    return fetch("/cart/change", config)
      .then((response) => response.json())
      .then((response) => {
        if (price) {
          price.classList.remove("hidden");
        }
        if (loader) {
          loader.classList.add("hidden");
        }

        this.renderCartSections(response.sections);
      })
      .catch(() => {
        // handle error
      });
  }

  async fetchCart() {
    const uri = window.Shopify.routes.root + "cart.js";
    const resp = await fetch(uri, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    return await resp.json();
  }
}

customElements.define("site-cart", SiteCart);

/*
Site Marquee
*/

class SiteMarquee extends HTMLElement {
  constructor() {
    super();
    const duration = this.dataset.duration;
    const shadowRoot = this.attachShadow({ mode: "open" });
    shadowRoot.innerHTML = this.getInnerHTML();

    const width = this.querySelector('[slot="content"]').scrollWidth / 5;
    shadowRoot.innerHTML = this.getInnerHTML(width, duration);
  }

  getInnerHTML(width = 0, duration = "10s") {
    return `
      <style>
        ::slotted([slot="content"]) {
          display: grid;
          grid-auto-flow: column;
          align-items: center;
          height: 100%;
        }
        .marquee-list {
          display: grid;
          grid-auto-flow: column;
          align-items: center;
          white-space: nowrap;
          animation: marquee ${duration} linear infinite;
          height: 100%;
        }

        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-${width}px);
          }
        }
      </style>
      <div class="marquee-cont">
        <div class="marquee-list">
          <slot name="content"></slot>
        </div>
      </div>
    `;
  }
}

customElements.define("site-marquee", SiteMarquee);

/*
Klaviyo Signup
*/

class KlaviyoSignup extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("form");
    this.form.addEventListener("submit", (e) => {
      e.preventDefault();
      this.submitForm(e);
    });
  }

  async submitForm(e) {
    const listId = "VHEHch";
    const email = this.form.querySelector('[type="email"]');
    const submit = this.form.querySelector(
      '[type="submit"] > span:first-child'
    );

    submit.innerHTML = "Subscribing...";

    const body = { email: email.value, listId };

    const config = {
      body: JSON.stringify(body),
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: `application/json`,
      },
    };

    if (listId) {
      try {
        fetch(
          "https://hunni-serverless.vercel.app/api/rumer-has-it/subscribe",
          config
        )
          .then((res) => res.json())
          .then((data) => {
            this.form.reset();
            submit.innerHTML = "Subscribe";

            const disclaimer = this.querySelector(".disclaimer");
            const success = this.querySelector(".success");
            disclaimer.classList.add("hidden");
            success.classList.remove("hidden");
          });
      } catch (e) {
        console.log(e.message);
      }
    }
  }
}

customElements.define("klaviyo-signup", KlaviyoSignup);
